import React from 'react'
import {useNavigate} from 'react-router-dom'
import {ChatNotification, StatusNotification} from '../../../types/notificationTypes'
import {BiMessageDetail} from 'react-icons/bi'
import {MdOutlineNotificationsActive} from 'react-icons/md'
import axios from 'axios'
import {useAuth} from '../../modules/auth'
import {ensureBenefitDetail} from '../../../utils/ensureBenefitDetail'

interface NotificationCardProps {
  notification: ChatNotification | StatusNotification
  type: 'chat' | 'status'
  index: number
}

/**
 * Notification Card Component for Dashboard
 * Displays a single notification item with claim number, message, and time
 */
const NotificationCard: React.FC<NotificationCardProps> = ({notification, type, index}) => {
  const navigate = useNavigate()
  const {auth} = useAuth()

  const isChatNotification = (n: ChatNotification | StatusNotification): n is ChatNotification => {
    return 'latestRole' in n
  }

  const handleClick = async () => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Provider/GetByClaimNumber/${notification.claimNumber}`
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${auth?.token}`,
        },
      })

      if (response.data) {
        if (!ensureBenefitDetail(response.data)) return

        const path = response.data?.treatmentType === 'Rawat jalan'
        ? '/outpatient'
        : '/inpatient';

        navigate(path, {state: {claimDetails: response.data}})
      }
    } catch (error) {
      console.error('Error fetching claim details:', error)
      navigate('/inpatient', {state: {claimNumber: notification.claimNumber}})
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(`${dateString}Z`)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  const isUnread = !notification.isOpen

  return (
    <div
      className={`dashboard-notification-card ${isUnread ? 'unread' : ''}`}
      onClick={handleClick}
      style={{
        minWidth: '260px',
        maxWidth: '300px',
        background: isUnread ? 'linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%)' : '#ffffff',
        borderRadius: '12px',
        padding: '16px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
        borderLeft: isUnread ? `3px solid ${type === 'chat' ? '#3699ff' : '#ffc700'}` : 'none',
      }}
    >
      {/* Header with icon and claim number */}
      <div className='d-flex align-items-center gap-3 mb-3'>
        <div
          className={`d-flex align-items-center justify-content-center ${
            type === 'chat' ? 'bg-light-primary' : 'bg-light-warning'
          }`}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            flexShrink: 0,
          }}
        >
          {type === 'chat' ? (
            <BiMessageDetail size={20} className='text-primary' />
          ) : (
            <MdOutlineNotificationsActive size={20} className='text-warning' />
          )}
        </div>
        <div className='flex-grow-1 min-w-0'>
          <div className='d-flex align-items-center justify-content-between'>
            <span className='fw-bold text-dark fs-7'>{notification.claimNumber}</span>
            {isUnread && (
              <span
                className={`badge ${type === 'chat' ? 'badge-primary' : 'badge-warning'}`}
                style={{fontSize: '10px'}}
              >
                New
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className='mb-3'>
        {!isChatNotification(notification) && (notification as StatusNotification).status && (
          <span className='badge badge-light-info mb-2' style={{fontSize: '10px', fontWeight: 500}}>
            {(notification as StatusNotification).status}
          </span>
        )}
        <p
          className='text-gray-600 fs-8 mb-0'
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: '1.5',
          }}
        >
          {isChatNotification(notification)
            ? notification.remark || 'New message'
            : (notification as StatusNotification).remark || 'Status updated'}
        </p>
      </div>

      {/* Footer */}
      <div className='d-flex justify-content-between align-items-center'>
        <span className='text-gray-500 fs-9 text-truncate' style={{maxWidth: '140px'}}>
          {'insurerName' in notification ? notification.insurerName : notification.providerName}
        </span>
        <span className='text-gray-400 fs-9'>{formatDate(notification.createdAt)}</span>
      </div>
    </div>
  )
}

export default NotificationCard
